import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Package, Plus, Eye, ShoppingCart } from "lucide-react";

export default function ProductsModal({ isOpen, onClose, book, products, productComponents, onRefresh }) {
  const [bookProducts, setBookProducts] = useState([]);

  useEffect(() => {
    if (book && isOpen) {
      loadBookProducts();
    }
  }, [book, isOpen, products, productComponents]);

  const loadBookProducts = () => {
    if (!book) return;
    
    const bookComponentsData = productComponents.filter(pc => pc.book_id === book.id);
    const productsList = bookComponentsData.map(component => {
      const product = products.find(p => p.id === component.product_id);
      return product ? { 
        ...product, 
        quantity: component.quantity,
        componentId: component.id 
      } : null;
    }).filter(Boolean);
    
    setBookProducts(productsList);
  };

  if (!book) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Package className="w-5 h-5 text-amber-600" />
            Комплекты с книгой: {book.title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {bookProducts.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="p-8 text-center">
                <ShoppingCart className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Эта книга не входит в комплекты
                </h3>
                <p className="text-slate-600 mb-4">
                  Создайте товары в разделе "Склад" и добавьте эту книгу в состав комплектов для продажи.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {bookProducts.map((product) => (
                <Card key={product.id} className="border-slate-200">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg text-slate-900 mb-1">
                          {product.name}
                        </CardTitle>
                        {product.description && (
                          <p className="text-slate-600 text-sm line-clamp-2">
                            {product.description}
                          </p>
                        )}
                      </div>
                      <div className="text-right ml-4">
                        <p className="text-2xl font-bold text-slate-900">
                          ₽{product.price.toLocaleString()}
                        </p>
                        {product.sku && (
                          <p className="text-sm text-slate-500">
                            SKU: {product.sku}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                          <Badge className="bg-purple-100 text-purple-800">
                            {product.type === 'bundle' ? 'Комплект' : 'Отдельный товар'}
                          </Badge>
                          <Badge variant="outline" className="border-blue-200 text-blue-800">
                            Количество: {product.quantity} экз.
                          </Badge>
                        </div>
                        {product.is_active ? (
                          <Badge className="bg-green-600 text-white border-green-700">
                            Активен
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-600 text-white border-gray-700">
                            Неактивен
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button onClick={onClose} className="px-6">
            Закрыть
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}